import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { addDays, format as formatDate, parseISO } from 'date-fns';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useDashboardUser } from '@/routes/dashboard/DashboardRouter';
import { fetchAdminSlots, setShowingSlot, type ShowingSlotAdmin } from '@/lib/services/showings';
import {
  buildBaseTimes,
  getNextWeekday,
  getWeekDates,
  getWeekStart,
  normalizeSlotTime,
} from '@/components/dashboard/showings/showingsAvailabilityWeek';

type SlotUpdate = { date: string; time: string; isAvailable: boolean };

export type ShowingAvailabilityEditMode = 'toggle' | 'available' | 'blocked';

export interface UseShowingsAvailabilityWeekResult {
  weekStart: string;
  weekDates: string[];
  customDate: string;
  customTime: string;
  slotMaps: Record<string, Map<string, ShowingSlotAdmin>>;
  timeRows: string[];
  isLoading: boolean;
  isError: boolean;
  isUpdating: boolean;
  setWeekStartFromInput: (value: string) => void;
  shiftWeek: (days: number) => void;
  toggleSlot: (
    date: string,
    time: string,
    shiftKey: boolean,
    mode?: ShowingAvailabilityEditMode,
  ) => void;
  setCustomDate: (date: string) => void;
  setCustomTime: (time: string) => void;
  addCustomSlotDisabled: boolean;
  addCustomSlot: () => void;
}

export function useShowingsAvailabilityWeek(): UseShowingsAvailabilityWeekResult {
  const user = useDashboardUser();
  const queryClient = useQueryClient();
  const [weekStart, setWeekStart] = useState(() => getWeekStart(getNextWeekday()));
  const weekDates = useMemo(() => getWeekDates(weekStart), [weekStart]);
  const [customDate, setCustomDate] = useState(() => weekDates[0] ?? weekStart);
  const [customTime, setCustomTime] = useState('');
  const lastSelectedRef = useRef<{ date: string; time: string } | null>(null);

  useEffect(() => {
    setCustomDate(weekDates[0] ?? weekStart);
  }, [weekDates, weekStart]);

  const slotsQuery = useQuery({
    queryKey: ['showing-availability-week', weekStart],
    queryFn: async ({ signal }) => {
      const entries = await Promise.all(
        getWeekDates(weekStart).map(async (date) => ({
          date,
          slots: await fetchAdminSlots({ user, date, signal }),
        })),
      );

      const slotsByDate: Record<string, ShowingSlotAdmin[]> = {};
      entries.forEach((entry) => {
        slotsByDate[entry.date] = entry.slots;
      });
      return slotsByDate;
    },
    staleTime: 30_000,
  });

  const setSlotsMutation = useMutation<undefined, unknown, SlotUpdate[]>({
    mutationFn: async (updates) => {
      for (const update of updates) {
        await setShowingSlot({
          user,
          date: update.date,
          time: update.time,
          isAvailable: update.isAvailable,
          note: null,
        });
      }
      return undefined;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['showing-availability-week'] });
    },
    onError: (error: unknown) => {
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        toast.error('Unable to update availability.');
      }
    },
  });

  const slotsByDate = useMemo<Record<string, ShowingSlotAdmin[]>>(
    () => slotsQuery.data ?? {},
    [slotsQuery.data],
  );

  const slotMaps = useMemo(() => {
    const maps: Record<string, Map<string, ShowingSlotAdmin>> = {};
    weekDates.forEach((date) => {
      const map = new Map<string, ShowingSlotAdmin>();
      (slotsByDate[date] ?? []).forEach((slot) => {
        const normalizedTime = normalizeSlotTime(slot.slotTime);
        if (normalizedTime.length === 0) return;
        map.set(normalizedTime, { ...slot, slotTime: normalizedTime });
      });
      maps[date] = map;
    });
    return maps;
  }, [slotsByDate, weekDates]);

  const baseTimes = useMemo(() => buildBaseTimes(8, 18), []);

  const timeRows = useMemo(() => {
    const times = new Set<string>(baseTimes);
    weekDates.forEach((date) => {
      (slotsByDate[date] ?? []).forEach((slot) => {
        const normalizedTime = normalizeSlotTime(slot.slotTime);
        if (normalizedTime.length === 0) return;
        times.add(normalizedTime);
      });
    });
    return Array.from(times).sort((a, b) => a.localeCompare(b));
  }, [baseTimes, slotsByDate, weekDates]);

  const toggleSlot = (
    date: string,
    time: string,
    shiftKey: boolean,
    mode: ShowingAvailabilityEditMode = 'toggle',
  ): void => {
    if (setSlotsMutation.isPending) return;

    const normalizedTime = normalizeSlotTime(time);
    const status = slotMaps[date].get(normalizedTime)?.status ?? 'blocked';
    if (status === 'booked') return;

    const targetIsAvailable =
      mode === 'toggle' ? status !== 'available' : mode === 'available' ? true : false;
    const previous = lastSelectedRef.current;

    const buildUpdate = (targetTime: string): SlotUpdate | null => {
      const targetStatus = slotMaps[date].get(targetTime)?.status ?? 'blocked';
      if (targetStatus === 'booked') return null;
      const isCurrentlyAvailable = targetStatus === 'available';
      if (isCurrentlyAvailable === targetIsAvailable) return null;
      return { date, time: targetTime, isAvailable: targetIsAvailable };
    };

    let updates: SlotUpdate[] = [];

    if (shiftKey && previous?.date === date) {
      const startIndex = timeRows.indexOf(previous.time);
      const endIndex = timeRows.indexOf(normalizedTime);

      if (startIndex !== -1 && endIndex !== -1) {
        const from = Math.min(startIndex, endIndex);
        const to = Math.max(startIndex, endIndex);
        updates = timeRows
          .slice(from, to + 1)
          .map((rangeTime) => buildUpdate(rangeTime))
          .filter((update): update is SlotUpdate => update !== null);
      }
    }

    if (updates.length === 0) {
      const update = buildUpdate(normalizedTime);
      if (update) {
        updates = [update];
      }
    }

    lastSelectedRef.current = { date, time: normalizedTime };
    if (updates.length === 0) return;

    setSlotsMutation.mutate(updates);
  };

  const shiftWeek = (days: number): void => {
    const parsed = parseISO(weekStart);
    if (Number.isNaN(parsed.valueOf())) return;
    setWeekStart(formatDate(addDays(parsed, days), 'yyyy-MM-dd'));
  };

  const setWeekStartFromInput = (value: string): void => {
    setWeekStart(getWeekStart(value));
  };

  const addCustomSlotDisabled = customTime.trim().length === 0 || setSlotsMutation.isPending;

  const addCustomSlot = (): void => {
    const normalizedTime = normalizeSlotTime(customTime.trim());
    if (normalizedTime.length === 0) return;
    setSlotsMutation.mutate([{ date: customDate, time: normalizedTime, isAvailable: true }]);
    setCustomTime('');
  };

  return {
    weekStart,
    weekDates,
    customDate,
    customTime,
    slotMaps,
    timeRows,
    isLoading: slotsQuery.isLoading,
    isError: slotsQuery.isError,
    isUpdating: setSlotsMutation.isPending,
    setWeekStartFromInput,
    shiftWeek,
    toggleSlot,
    setCustomDate,
    setCustomTime,
    addCustomSlotDisabled,
    addCustomSlot,
  };
}
